import React from 'react';
import styled from 'styled-components6';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIcon, getIconColor } from '../../../../utils';
import { IconOption } from 'rwl-shared/dist/models/widget';

type ServiceAreaMarkerContentProps = {
    icon: IconOption;
    iconTint: string;
    jobCount: number;
    isCluster?: boolean;
    onClick?: () => void;
};

const getContrastCountColor = (
    icon: IconOption,
    colors: { fill: string; backDrop: string }
) => {
    switch (icon) {
        case IconOption.House:
            return colors.backDrop;
        case IconOption.PointerDark:
            return 'white';
        case IconOption.PointerLight:
        default:
            return 'var(--rwl-text-default)';
    }
};

const getCountColor = (icon: IconOption, iconTint: string) => {
    switch (icon) {
        case IconOption.PointerDark:
            return getIconColor(icon, iconTint).fill;
        case IconOption.PointerLight:
            return getIconColor(icon, iconTint).fill;
        case IconOption.House:
        default:
            return 'var(--rwl-text-default)';
    }
};

export const JobCountSpan = styled.span`
    font-size: 12px;
    font-weight: 500;
`;

export const MarkerContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'colors' && prop !== 'icon', // Prevents console warnings for invalid props
})<{ colors: { fill: string; backDrop: string }; icon: IconOption }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ colors }) => colors.backDrop};
    border-radius: var(--rwl-radius-md);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    padding: calc(var(--rwl-spacing-xs) * 0.5) calc(var(--rwl-spacing-xs) + 1px);
    transition: background 0.3s ease, color 0.3s ease;
    height: 17px;
    min-width: 38px;
    cursor: pointer;
    > * {
        cursor: pointer;
    }

    > span {
        color: ${({ icon, colors }) => getCountColor(icon, colors.fill)};
        margin-left: var(--rwl-spacing-xs);
    }
    &:hover {
        background: ${({ colors }) => colors.fill};
        > span {
            color: ${({ colors, icon }) => getContrastCountColor(icon, colors)};
        }
        > svg {
            color: ${({ colors }) => colors.backDrop};
            transition: color 0.3s ease;
        }
    }
`;

const BaseContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const ClusterBackdrop = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'colors', // Prevents console warnings for invalid props
})<{ colors: { backDrop: string } }>`
    position: absolute;
    top: 2px;
    left: 2px;
    z-index: -1;
    background: 'white';
    border-radius: var(--rwl-radius-md);
    width: 100%;
    height: 100%;
    box-shadow: 0 0px 5px rgba(0, 0, 0, 0.12);
    background: ${({ colors }) => colors.backDrop};
`;

const buildIcon = (icon: IconOption, iconTint: string, isCluster: boolean) => {
    return isCluster ? (
        <span className="fa-layers fa-fw">
            <FontAwesomeIcon
                icon={getIcon(icon)}
                color={getIconColor(icon, iconTint).fill}
                size="1x"
                fontSize={12}
                transform="right-3 up-3"
                stroke={'#FFFFFF'}
                strokeWidth={1}
            />
            <FontAwesomeIcon
                // Using this as a 'stroke' since the stroke attribute does not seem
                // to actually apply anything when used with FontAwesomeIcon
                icon={getIcon(icon)}
                color={getIconColor(icon, iconTint).backDrop}
                size="1x"
                fontSize={12}
                transform="grow-3"
            />
            <FontAwesomeIcon
                icon={getIcon(icon)}
                color={getIconColor(icon, iconTint).fill}
                size="1x"
                fontSize={12}
                stroke={'#FFFFFF'}
                strokeWidth={1}
            />
        </span>
    ) : (
        <FontAwesomeIcon
            icon={getIcon(icon)}
            color={getIconColor(icon, iconTint).fill}
            fontSize={12}
            size="1x"
        />
    );
};

const ServiceAreaMarkerContent: React.FC<ServiceAreaMarkerContentProps> = ({
    icon,
    iconTint,
    jobCount,
    isCluster = false,
    onClick,
}) => (
    <BaseContainer>
        {isCluster && <ClusterBackdrop colors={getIconColor(icon, iconTint)} />}
        <MarkerContainer
            icon={icon}
            colors={getIconColor(icon, iconTint)}
            onClick={onClick}
        >
            {buildIcon(icon, iconTint, isCluster)}
            <JobCountSpan>{jobCount || 0}</JobCountSpan>
        </MarkerContainer>
    </BaseContainer>
);

export default ServiceAreaMarkerContent;
